/**
 * Boss runner (FS-08 §4.6). Drives a `BossDef`'s phases against the AudioClock, quantised to **bars**
 * (R26). There is **no health bar** (R25) — the readout is the phase count and the bars remaining,
 * which is honest: the player is surviving a duration, and the UI says so.
 *
 * Phase transitions are telegraphed `BOSS_PHASE_TELEGRAPH` ahead (R29) through a non-motion,
 * non-hue channel — the HUD announces the incoming phase by name.
 */
import type { AudioClock } from '../../audio/clock';
import type { BossBeatContext, BossDef, BossEmitOptions } from './types';
import type { HazardKind } from '../../core/event-map';

const BEATS_PER_BAR = 4;
export const BOSS_PHASE_TELEGRAPH_BEATS = 4; // one bar of warning

export interface BossEmitFn {
  (kind: HazardKind, nodeTheta: number, intensity: number, opts?: BossEmitOptions): void;
}

export class BossRunner {
  def: BossDef | null = null;
  phaseIndex = 0;
  done = false;
  active = false;

  private phaseStartBeat = 0;
  private lastBeat = -1;
  private ctx: BossBeatContext & {
    beat: number;
    bar: number;
    beatInBar: number;
    phaseProgress: number;
  };

  constructor() {
    // Mutable context reused every beat — allocation-free (FS-02 R27).
    this.ctx = {
      beat: 0,
      bar: 0,
      beatInBar: 0,
      phaseProgress: 0,
      emit: () => {},
    };
  }

  start(def: BossDef, clock: AudioClock): void {
    this.def = def;
    this.active = true;
    this.done = false;
    this.phaseIndex = 0;
    this.phaseStartBeat = Math.floor(clock.beat);
    this.lastBeat = this.phaseStartBeat - 1;
  }

  stop(): void {
    this.active = false;
    this.def = null;
  }

  get name(): string {
    return this.def?.name ?? '';
  }

  get phaseCount(): number {
    return this.def?.phases.length ?? 0;
  }

  get phaseName(): string {
    if (!this.def) return '';
    return this.def.phases[Math.min(this.phaseIndex, this.def.phases.length - 1)]!.name;
  }

  /** Bars left in the current phase — the honest progress readout (R25). */
  barsRemaining(clock: AudioClock): number {
    if (!this.def) return 0;
    const phase = this.def.phases[Math.min(this.phaseIndex, this.def.phases.length - 1)]!;
    const elapsed = (clock.beat - this.phaseStartBeat) / BEATS_PER_BAR;
    return Math.max(0, Math.ceil(phase.bars - elapsed));
  }

  /** Name of the phase about to begin, once inside the telegraph window (R29). Else null. */
  incomingPhase(clock: AudioClock): string | null {
    if (!this.def) return null;
    const phase = this.def.phases[this.phaseIndex];
    const next = this.def.phases[this.phaseIndex + 1];
    if (!phase || !next) return null;
    const beatsElapsed = clock.beat - this.phaseStartBeat;
    const beatsTotal = phase.bars * BEATS_PER_BAR;
    return beatsTotal - beatsElapsed <= BOSS_PHASE_TELEGRAPH_BEATS ? next.name : null;
  }

  /** Music level for the current phase (The Silent Node). */
  get musicLevel(): number {
    const p = this.def?.phases[Math.min(this.phaseIndex, this.phaseCount - 1)];
    return p?.musicLevel ?? 1;
  }

  /** Cosmetic HUD jam for the current phase (The Jammer). Never affects values. */
  get hudJam(): boolean {
    const p = this.def?.phases[Math.min(this.phaseIndex, this.phaseCount - 1)];
    return p?.hudJam === true;
  }

  /** Tempo override for the current phase, or 0 for none. */
  get phaseBpm(): number {
    const p = this.def?.phases[Math.min(this.phaseIndex, this.phaseCount - 1)];
    return p?.bpm ?? 0;
  }

  update(clock: AudioClock, emit: BossEmitFn): void {
    if (!this.def || this.done || !this.active) return;
    const phase = this.def.phases[this.phaseIndex]!;

    const currentBeat = Math.floor(clock.beat);
    if (currentBeat > this.lastBeat) {
      this.lastBeat = currentBeat;
      const beatsElapsed = currentBeat - this.phaseStartBeat;
      if (beatsElapsed >= 0) {
        const c = this.ctx;
        c.beat = beatsElapsed;
        c.bar = Math.floor(beatsElapsed / BEATS_PER_BAR);
        c.beatInBar = beatsElapsed % BEATS_PER_BAR;
        c.phaseProgress = Math.min(1, beatsElapsed / (phase.bars * BEATS_PER_BAR));
        c.emit = emit;
        phase.onBeat(c);
      }
    }

    // Advance phase on a bar boundary.
    if (clock.beat - this.phaseStartBeat >= phase.bars * BEATS_PER_BAR) {
      this.phaseIndex++;
      this.phaseStartBeat = Math.floor(clock.beat);
      if (this.phaseIndex >= this.def.phases.length) {
        this.done = true;
        this.active = false;
      }
    }
  }
}
